import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Params } from '@angular/router';
import { PermissionPipe } from '@app/features/admin/settings/permission/pipes/permission.pipe';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { SelectDateComponent } from '@shared/components/form/select-date/select-date.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { SelectionButtonComponent } from '@shared/components/selection-button/selection-button.component';
import { buttons } from '@vessel-reports/noon/noon.constants';
import { NoonActions } from '@vessel-reports/noon/state/noon.actions';
import { NoonState } from '@vessel-reports/noon/state/noon.state';
import { Button } from '@vessel-reports/noon/state/noon.types';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { map } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import { Observable, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'create-noon-report',
  imports: [
    TranslocoPipe,
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    FooterComponent,
    SelectDateComponent,
    TextboxComponent,
    SelectionButtonComponent,
    MatIcon,
    ReactiveFormsModule,
    PermissionPipe
  ],
  templateUrl: './create-noon-report.component.html',
  styleUrl: './create-noon-report.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateNoonReportComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  buttons: WritableSignal<Button[]> = signal(cloneDeep(buttons));
  @Output() save: EventEmitter<void> = new EventEmitter<void>(); // declare output
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  queryParams: WritableSignal<Params> = signal(void 0);
  form: UntypedFormGroup;
  // Injects
  private readonly _noonReportState: NoonState = inject(NoonState);
  private readonly _noonReportActions: NoonActions = inject(NoonActions);
  private readonly _profileState: ProfileState = inject(ProfileState);
  private readonly _noonReportsActions: VesselReportsAction = inject(VesselReportsAction);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly isEmpty: typeof isEmpty = isEmpty;
  currentStatusIndex: string;
  previousNoonReport: Signal<any> = computed(() => this._noonReportState.previousReport());
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(private _formBuilder: FormBuilder) {
    this.form = this._formBuilder.group({
      report_type_id: ['', Validators.required],
      date_report: [dayjs().unix(), Validators.required],
      voyage_number: ['', Validators.required],
      vessel_id: [this._profileState.profile().ids_vessel_id, Validators.required]
    });
  }

  ngOnInit() {
    this._subscribeToSource$(this._activatedRoute.paramMap, (paramMap) => {
      this.currentStatusIndex = paramMap.get('status');
    });

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set({ ...queryParams });
    });

    if (this.previousNoonReport()) {
      this.form.patchValue({
        report_type_id: this.previousNoonReport()?.report_type_id,
        voyage_number: this.previousNoonReport()?.voyage_number
      });
      const newButtons = map(this.buttons(), (button: Button) => {
        if (button.value === this.previousNoonReport()?.report_type_id) {
          button.active = true;
        }
        return button;
      });
      this.buttons.set(newButtons);
    }
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  get previousTitleReport() {
    if (isEmpty(this.previousNoonReport())) {
      return '';
    }
    const title: string =
      dayjs(this.previousNoonReport()?.date_report * 1000).format('DD MMM YYYY') +
      `@${this.previousNoonReport()?.report_type_id === 1 ? 'Sea' : 'Port'} ` +
      `(Voy# : ${this.previousNoonReport()?.voyage_number})`;
    return title;
  }

  onClosePress: FunctionType = () => {
    this.close?.emit();
  };

  onDiscard: FunctionType = () => {
    this.close?.emit();
  };

  onSavePress: FunctionType = () => {
    const body = this.form.getRawValue();

    this._noonReportActions.actions.createNewNoonReport({
      body
    });
    this._noonReportActions.actions.createNewNoonReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
      this._noonReportsActions.actions.getVesselReports({
        queryParams: this.queryParams(),
        type: '1,2'
      });

      this.save.emit();
    });
  };

  onButtonsChange(buttons: Button[]) {
    const activeButton = buttons.find((button) => button.active);
    if (activeButton) {
      this.form.patchValue({
        report_type_id: activeButton.value
      });
    }
  }

  private _subscribeToSource$<T>(source$: Observable<T>, callback: (value: T) => void): void {
    source$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: T) => {
      callback(value);

      this._changeDetectorRef.markForCheck();
    });
  }
}
